import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  type ReactNode,
} from "react";
import { useFontenehusApi } from "../api/FontenehusApi";
import type { Fontenehus } from "../types/Fontenehus";
import { handleApiError } from "../utils/handleApiError";

interface FontenehusContextType {
  fontenehusList: Fontenehus[];
  loading: boolean;
  selectedFontenehus: Fontenehus | null;
  setSelectedFontenehus: React.Dispatch<
    React.SetStateAction<Fontenehus | null>
  >;
  refreshFontenehus: () => Promise<void>;
  refreshTrigger: number;
  forceRefresh: () => void; // ✅ Make sure this exists
}

const FontenehusContext = createContext<FontenehusContextType | undefined>(
  undefined,
);

export const FontenehusProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const { getAllFontenehus } = useFontenehusApi();
  const [fontenehusList, setFontenehusList] = useState<Fontenehus[]>([]);
  const [loading, setLoading] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState<number>(0);

  // ✅ Initialize from user localStorage (if available)
  const [selectedFontenehus, setSelectedFontenehusState] =
    useState<Fontenehus | null>(() => {
      const stored = localStorage.getItem("user");
      if (!stored) return null;
      try {
        const parsed = JSON.parse(stored);
        if (parsed.fontenehusId && parsed.fontenehusName) {
          return {
            id: parsed.fontenehusId,
            name: parsed.fontenehusName,
            fontenehusCode: parsed.fontenehusCode,
          };
        }
        return null;
      } catch {
        return null;
      }
    });

  // ✅ Wrap setter to trigger refresh
  const setSelectedFontenehus: React.Dispatch<
    React.SetStateAction<Fontenehus | null>
  > = (value) => {
    setSelectedFontenehusState((prev) => {
      const newVal =
        typeof value === "function"
          ? (value as (prev: Fontenehus | null) => Fontenehus | null)(prev)
          : value;
      if (newVal) {
        localStorage.setItem("selectedFontenehus", JSON.stringify(newVal));
      } else {
        localStorage.removeItem("selectedFontenehus");
      }
      setRefreshTrigger((t) => t + 1); // 👈 trigger refresh for dependents
      return newVal;
    });
  };

  const fetchFontenehus = async () => {
    setLoading(true);
    try {
      const response = await getAllFontenehus();
      setFontenehusList(response.data.data);
    } catch (err: any) {
      handleApiError(err, "Kunne ikke hente Fontenehus.");
    } finally {
      setLoading(false);
    }
  };

  const refreshFontenehus = async () => {
    await fetchFontenehus();
    setRefreshTrigger((t) => t + 1);
  };

  useEffect(() => {
  fetchFontenehus();
}, []);

  // ✅ Add forceRefresh — lightweight trigger without refetching list
  const forceRefresh = () => setRefreshTrigger((t) => t + 1);

  return (
    <FontenehusContext.Provider
      value={{
        fontenehusList,
        loading,
        selectedFontenehus,
        setSelectedFontenehus,
        refreshFontenehus,
        refreshTrigger,
        forceRefresh, // ✅ Add this to the provided value
      }}
    >
      {children}
    </FontenehusContext.Provider>
  );
};

export const useFontenehus = (): FontenehusContextType => {
  const context = useContext(FontenehusContext);
  if (!context)
    throw new Error("useFontenehus må brukes inne i FontenehusProvider");
  return context;
};
